// Backordered Orders Page

import { useState } from "react";
import PropTypes from "prop-types";
import {
	useEffect,
	useRef,
	useMemo,
	useSelector,
	useDispatch,
	useRBackorderedOrdersMutation,
	useUOrdersMutation,
	useUCartItemStatusMutation,
	useRCustomersMutation,
} from "custom_exports/all_hooks";
import { Box, TextField, InputAdornment } from "custom_exports/material_ui_items";
import SearchIcon from "@mui/icons-material/Search";
import {
	orderActions,
	backorderedOrderActions,
	customerActions,
	getBackorderedOrderItemsAsList,
	getCustomerItemsAsList,
	getBackorderedOrderPagination,
} from "services/redux/reducers/slice";
import { authSelectors } from "services/redux/reducers";
import ShowStatus from "components/common/ShowStatus";
import { callApi } from "common/api_handlers/api_call_manager";
import OrdersAccordionView from "components/pages/orders/accordion/OrdersAccordionView";
import Swal from "sweetalert2";
import { THEME } from "app/settings/settings";
import K from "common/keywords";
import C from "common/constants";

import Pagination from "components/common/iui/pagination/Pagination";

function BackorderedOrdersPage(props) {
	//--------------------------------- variables ---------------------------------//
	// ---- initializations ---- //
	// ---- destructuring ---- //
	const { updateOrder } = orderActions;
	const { setBackorderedOrder, updateBackorderedOrder } = backorderedOrderActions;
	const { setCustomer } = customerActions;

	// ---- assignments ---- //
	const dispatch = useDispatch();
	const [searchQuery, setSearchQuery] = useState("");
	const [RBackorderedOrdersApiCaller, RBackorderedOrdersApiResult] = useRBackorderedOrdersMutation();
	const [UOrdersApiCaller, UOrdersApiResult] = useUOrdersMutation();
	const [UCartItemStatusApiCaller, UCartItemStatusApiResult] = useUCartItemStatusMutation();
	const [RCustomersApiCaller, RCustomersApiResult] = useRCustomersMutation();
	const user = useSelector(authSelectors.getUser);
	const ordersAsList = useSelector(getBackorderedOrderItemsAsList);
	const customersAsList = useSelector(getCustomerItemsAsList);
	const pagination = useSelector(getBackorderedOrderPagination);

	// Request cancellation ref
	const abortControllerRef = useRef(null);

	//--------------------------------- functions ---------------------------------//
	// ---- api managers ---- //
	const onApiResponse = ({ apiCallerId, ...response }) => {
		const { data, meta_data } = response || {};
		if (apiCallerId === K.RBackorderedOrders) {
			// Store in separate backorderedOrder state
			dispatch(setBackorderedOrder({ data, pagination: meta_data?.pagination }));
		} else if (apiCallerId === K.UOrders) {
			// Update both states to keep them in sync
			dispatch(updateOrder(data?.orders?.items || []));
			dispatch(updateBackorderedOrder(data?.orders?.items || []));
		} else if (apiCallerId === K.UCartItemStatus) {
			// Update both states to keep them in sync
			dispatch(updateOrder(data?.orders?.items || []));
			dispatch(updateBackorderedOrder(data?.orders?.items || []));
		} else if (apiCallerId === K.RCustomers) {
			dispatch(setCustomer(data));
		} else {
			console.log("onApiResponse is not defined for " + apiCallerId);
		}
	};

	const onApiError = ({ apiCallerId, ...error }) => {
		console.log(error);
		if (apiCallerId === K.UOrders) {
			Swal.fire({
				text: "Order Updation Failed, Something Went Wrong..",
				icon: "error",
				confirmButtonColor: THEME.app.colors.red.main,
			});
		} else if (apiCallerId === K.UCartItemStatus) {
			Swal.fire({
				text: "Cart Item Status Update Failed, Something Went Wrong..",
				icon: "error",
				confirmButtonColor: THEME.app.colors.red.main,
			});
		} else if (apiCallerId === K.RBackorderedOrders) {
			Swal.fire({
				text: "Failed to Load Backordered Orders. Please Try Again.",
				icon: "error",
				confirmButtonColor: THEME.app.colors.red.main,
			});
		} else if (apiCallerId === K.RCustomers) {
			Swal.fire({
				text: "Failed to Load Customer Data. Please Try Again.",
				icon: "error",
				confirmButtonColor: THEME.app.colors.red.main,
			});
		}
	};

	const apiActions = { onApiResponse, onApiError };

	const callRBackorderedOrdersApi = (apiInputParams, apiCallerId) => {
		// Cancel previous request if it exists
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		// Create new abort controller for this request
		abortControllerRef.current = new AbortController();

		callApi({
			apiCallerId: apiCallerId ?? K.RBackorderedOrders,
			apiCaller: RBackorderedOrdersApiCaller,
			apiInputParams,
			apiActions,
		});
	};

	const callUOrdersApi = (apiInputParams, apiCallerId) => {
		callApi({
			apiCallerId: apiCallerId ?? K.UOrders,
			apiCaller: UOrdersApiCaller,
			apiInputParams,
			apiActions,
		});
	};

	const callUCartItemStatusApi = (apiInputParams, apiCallerId) => {
		callApi({
			apiCallerId: apiCallerId ?? K.UCartItemStatus,
			apiCaller: UCartItemStatusApiCaller,
			apiInputParams,
			apiActions,
		});
	};

	const callRCustomersApi = (apiInputParams, apiCallerId) => {
		callApi({
			apiCallerId: apiCallerId ?? K.RCustomers,
			apiCaller: RCustomersApiCaller,
			apiInputParams,
			apiActions,
		});
	};

	// Backend already filters backordered orders, apply search filter and sort by date
	const filteredOrdersAsList = useMemo(() => {
		let filtered = [...ordersAsList];

		// Filter by search query (name or email)
		const searchLower = searchQuery.trim().toLowerCase();
		if (searchLower) {
			filtered = filtered.filter((order) => {
				const businessName = order.placed_by_user_details?.profile?.business_name || "";
				const businessEmail = order.placed_by_user_details?.profile?.business_email || "";
				return (
					businessName.toLowerCase().includes(searchLower) ||
					businessEmail.toLowerCase().includes(searchLower)
				);
			});
		}

		// Sort by date
		return filtered.sort((a, b) => {
			// Safe date parsing with fallback
			const dateA = a.created_on ? Date.parse(a.created_on) : 0;
			const dateB = b.created_on ? Date.parse(b.created_on) : 0;
			return dateB - dateA;
		});
	}, [ordersAsList, searchQuery]);

	// ---- handlers ---- //
	const handleOnClick = (id, data) => {
		if (id === K.UOrderStatus) {
			const apiParams = {
				method: "update_orders_statuses",
				id: data.orderId,
				status: data.newStatusId,
			};
			// Include notify_customer if provided (for backorder notifications)
			if (data.notifyCustomer !== undefined) {
				apiParams.notify_customer = data.notifyCustomer;
			}
			callUOrdersApi(apiParams);
		} else if (id === K.UCartItemStatus) {
			const apiParams = {
				cart_item_id: data.cartItemId,
				status: data.newStatusId,
			};
			// Include notify_customer if provided (for backorder notifications)
			if (data.notifyCustomer !== undefined) {
				apiParams.notify_customer = data.notifyCustomer;
			}
			callUCartItemStatusApi(apiParams);
		} else {
			console.log("unknown click id " + id);
		}
	};

	// ---- useEffects ---- //
	useEffect(() => {
		callRBackorderedOrdersApi(); // Fetch backordered orders on mount

		// Cleanup: abort pending requests on unmount
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (
			user?.role &&
			user.role !== C.user_roles.customer &&
			customersAsList.length <= 0
		) {
			callRCustomersApi();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	//--------------------------------- contents ----------------------------------//
	const loadingIndication = (
		<ShowStatus
			statusType="loading"
			extraSX={{ bgcolor: "white", zIndex: 1000 }}
		/>
	);

	const content = (
		<>
			{RBackorderedOrdersApiResult.isLoading && loadingIndication}
			<Box>
				{/* Search Bar */}
				<Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
					<TextField
						size="small"
						sx={{ width: "300px" }}
						variant="outlined"
						placeholder="Search by name or email..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
					/>
				</Box>

				<OrdersAccordionView
					orders={filteredOrdersAsList}
					handleOnClick={handleOnClick}
					isBackorderedView={true}
				/>
				<Pagination
					style={{ justifyContent: "flex-end", mt: 2 }}
					totalRecords={pagination?.totalRecords || 0}
					totalPages={pagination?.totalPages || 1}
					currentPageNumber={pagination?.pageNumber || 1}
					currentDataLength={filteredOrdersAsList.length}
					pageSize={pagination?.pageSize || 30}
					onPageChange={(pageNumber) =>
						callRBackorderedOrdersApi({ page_number: pageNumber })
					}
					dataName="backordered orders"
				/>
			</Box>
		</>
	);

	return content;
}

BackorderedOrdersPage.propTypes = {
	// No props currently passed, but defining for future use
};

export default BackorderedOrdersPage;
